import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import './World.css';
import QuestDock from './ui/QuestDock';
import MotionPermissionPrompt from './ui/MotionPermissionPrompt';
import { getPanelViewport, WINDOW_BAR_HEIGHT } from './scene/worldMath';

// Chromium keeps an unlock cooldown after Escape; Safari needs the request
// directly in the user gesture. Queue only an explicit request to look around.
const POINTER_LOCK_COOLDOWN_MS = 1300;
const hasPointerLockCooldown = () => /\b(?:Chrome|Chromium|Edg|OPR)\//.test(navigator.userAgent);
let worldModule;
const loadWorld = () => {
  if (!worldModule) {
    worldModule = import('./scene/createPortfolioWorld').catch((error) => {
      worldModule = null;
      throw error;
    });
  }
  return worldModule;
};

export default function World({ siteRef }) {
  const [inputMode] = useState(() => window.matchMedia?.('(pointer: coarse)').matches ? 'touch' : 'mouse');
  const isTouch = inputMode === 'touch';
  const supportsMotion = typeof window.DeviceOrientationEvent !== 'undefined' && window.isSecureContext !== false;
  const needsMotionPermission = isTouch && supportsMotion
    && typeof window.DeviceOrientationEvent?.requestPermission === 'function';
  const [motionEnabled, setMotionEnabled] = useState(() => isTouch && supportsMotion
    && typeof window.DeviceOrientationEvent?.requestPermission !== 'function');
  const motionEnabledRef = useRef(motionEnabled);
  const motionPermissionGrantedRef = useRef(false);
  const [status, setStatus] = useState('loading');
  const [loadProgress, setLoadProgress] = useState(0);
  const [loadPhase, setLoadPhase] = useState('downloading');
  const [message, setMessage] = useState('');
  const [browserVisible, setBrowserVisible] = useState(true);
  const browserVisibleRef = useRef(true);
  const [motionPending, setMotionPending] = useState(false);
  const motionPendingRef = useRef(false);
  const [checkingMotionPermission, setCheckingMotionPermission] = useState(needsMotionPermission);
  const [showMotionPrompt, setShowMotionPrompt] = useState(needsMotionPermission);
  const motionPromptOpen = showMotionPrompt && !checkingMotionPermission && status === 'active' && !motionEnabled;
  const motionPromptOpenRef = useRef(motionPromptOpen);
  motionPromptOpenRef.current = motionPromptOpen;
  const [attempt, setAttempt] = useState(0);
  const dockElement = useMemo(() => {
    const element = document.createElement('div');
    element.className = 'world-dock-anchor';
    return element;
  }, []);
  const stageRef = useRef(null);
  const runtimeRef = useRef(null);
  const mountedRef = useRef(true);
  const blurredRef = useRef(false);
  const lockedRef = useRef(false);
  const lastUnlockRef = useRef(-Infinity);
  const captureTimerRef = useRef(null);
  const capturePendingRef = useRef(false);
  const captureIdRef = useRef(0);
  const captureUsesPromiseRef = useRef(false);
  const autoCaptureEnabledRef = useRef(true);
  const motionRequestIdRef = useRef(0);

  const syncActivity = useCallback(() => {
    const enabled = !document.hidden && !blurredRef.current;
    runtimeRef.current?.setInteractionEnabled(enabled && !motionPromptOpenRef.current);
    runtimeRef.current?.setActive(enabled);
  }, []);

  const cancelCapture = useCallback(() => {
    window.clearTimeout(captureTimerRef.current);
    captureTimerRef.current = null;
    capturePendingRef.current = false;
    captureIdRef.current += 1;
  }, []);

  const releasePointer = useCallback(() => {
    cancelCapture();
    if (document.pointerLockElement === stageRef.current) document.exitPointerLock();
    else if (runtimeRef.current) setStatus(isTouch ? 'active' : 'free');
  }, [cancelCapture, isTouch]);

  const pointerError = useCallback(() => {
    if (!mountedRef.current || !capturePendingRef.current || document.pointerLockElement === stageRef.current) return;
    cancelCapture();
    lockedRef.current = false;
    autoCaptureEnabledRef.current = false;
    setStatus('free');
    setMessage('Mouse look is unavailable. You can still use the cursor to browse.');
    syncActivity();
  }, [cancelCapture, syncActivity]);

  const capturePointer = useCallback((event) => {
    if (isTouch || !runtimeRef.current || capturePendingRef.current || document.pointerLockElement
      || document.hidden || blurredRef.current || event?.metaKey || event?.ctrlKey || event?.altKey || event?.shiftKey) return;
    if (!stageRef.current?.requestPointerLock) return;
    setMessage('');
    capturePendingRef.current = true;
    captureUsesPromiseRef.current = false;
    const captureId = ++captureIdRef.current;
    const requestCapture = () => {
      captureTimerRef.current = null;
      if (!mountedRef.current || captureId !== captureIdRef.current) return;
      try {
        const request = stageRef.current.requestPointerLock();
        captureUsesPromiseRef.current = typeof request?.catch === 'function';
        request?.catch(() => {
          if (captureId === captureIdRef.current) pointerError();
        });
      } catch {
        pointerError();
      }
    };
    const wait = hasPointerLockCooldown()
      ? Math.max(0, lastUnlockRef.current + POINTER_LOCK_COOLDOWN_MS - performance.now()) : 0;
    if (wait > 0) captureTimerRef.current = window.setTimeout(requestCapture, wait);
    else requestCapture();
  }, [isTouch, pointerError]);

  const capturePageClick = useCallback((event) => {
    const pointerType = event.nativeEvent.pointerType;
    if (isTouch || !runtimeRef.current || document.pointerLockElement || document.hidden || blurredRef.current
      || !stageRef.current?.requestPointerLock || !autoCaptureEnabledRef.current
      || event.button !== 0 || event.detail === 0 || (pointerType && pointerType !== 'mouse')
      || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey
      || event.target.closest?.('[data-quest-action]')) return;
    // The first click resumes mouse look without accidentally activating a
    // link or taskbar button underneath the newly centered cursor.
    event.preventDefault();
    event.stopPropagation();
    capturePointer(event);
  }, [capturePointer, isTouch]);

  const dismissMotionPrompt = () => {
    motionRequestIdRef.current += 1;
    motionPendingRef.current = false;
    setMotionPending(false);
    setShowMotionPrompt(false);
  };

  const enableMotion = async () => {
    if (!runtimeRef.current || checkingMotionPermission || motionPendingRef.current || motionEnabledRef.current) return;
    const requestId = ++motionRequestIdRef.current;
    motionPendingRef.current = true;
    setMotionPending(true);
    setMessage('');
    let granted = supportsMotion;
    try {
      if (!motionPermissionGrantedRef.current && typeof window.DeviceOrientationEvent?.requestPermission === 'function') {
        granted = await window.DeviceOrientationEvent.requestPermission() === 'granted';
      }
    } catch {
      granted = false;
    }
    if (!mountedRef.current || requestId !== motionRequestIdRef.current) return;
    motionPermissionGrantedRef.current = granted;
    motionPendingRef.current = false;
    setMotionPending(false);
    setShowMotionPrompt(false);
    motionEnabledRef.current = granted;
    setMotionEnabled(granted);
    runtimeRef.current?.setMotionEnabled(granted);
    if (!granted) setMessage('Motion is unavailable. You can still tap buttons and swipe to scroll.');
    syncActivity();
  };

  const toggleMotion = () => {
    if (!runtimeRef.current || checkingMotionPermission || motionPendingRef.current) return;
    if (!motionEnabledRef.current) {
      enableMotion();
      return;
    }
    motionEnabledRef.current = false;
    setMotionEnabled(false);
    runtimeRef.current.setMotionEnabled(false);
    setMessage('');
  };

  const toggleBrowser = () => {
    if (!runtimeRef.current) return;
    browserVisibleRef.current = !browserVisibleRef.current;
    setBrowserVisible(browserVisibleRef.current);
    runtimeRef.current.setBrowserVisible(browserVisibleRef.current);
  };

  useEffect(() => {
    if (!needsMotionPermission) return undefined;
    let cancelled = false;
    const checkPermission = async () => {
      let granted = false;
      try {
        // Outside a user gesture, this returns a saved decision or rejects
        // with NotAllowedError if a new native prompt would be needed.
        // Never probe during an active gesture, which could open that prompt.
        if (!navigator.userActivation?.isActive) {
          granted = await window.DeviceOrientationEvent.requestPermission() === 'granted';
        }
      } catch {
        // A fresh grant still needs the Enable motion button's trusted tap.
      }
      if (cancelled) return;
      motionPermissionGrantedRef.current = granted;
      motionEnabledRef.current = granted;
      setMotionEnabled(granted);
      runtimeRef.current?.setMotionEnabled(granted);
      if (granted) setShowMotionPrompt(false);
      setCheckingMotionPermission(false);
    };
    checkPermission();
    return () => { cancelled = true; };
  }, [needsMotionPermission]);

  useEffect(() => {
    syncActivity();
  }, [motionPromptOpen, syncActivity]);

  useEffect(() => {
    mountedRef.current = true;
    const pointerChange = () => {
      const locked = document.pointerLockElement === stageRef.current;
      const wasLocked = lockedRef.current;
      lockedRef.current = locked;
      if (locked && !wasLocked && !capturePendingRef.current) {
        document.exitPointerLock();
        return;
      }
      if (!locked && wasLocked) lastUnlockRef.current = performance.now();
      cancelCapture();
      if (runtimeRef.current) setStatus(locked || isTouch ? 'active' : 'free');
      if (locked) {
        autoCaptureEnabledRef.current = true;
        setMessage('');
        stageRef.current?.focus({ preventScroll: true });
      }
      syncActivity();
    };
    const pointerLockError = () => {
      if (!captureUsesPromiseRef.current && captureTimerRef.current === null) pointerError();
    };
    const suspend = () => {
      blurredRef.current = true;
      releasePointer();
      syncActivity();
    };
    const resume = () => {
      blurredRef.current = false;
      syncActivity();
    };
    const visibilityChange = () => { if (document.hidden) suspend(); else resume(); };
    const keyDown = (event) => {
      // macOS can consume the final key of Cmd+Shift+4/5 without delivering it
      // to the page or blurring the browser. Release as soon as Command is held,
      // before the native screenshot tool takes control of the mouse.
      if (event.key === 'Escape' || event.key === 'Meta' || event.metaKey || event.key === 'PrintScreen') {
        releasePointer();
      }
    };
    document.addEventListener('pointerlockchange', pointerChange);
    document.addEventListener('pointerlockerror', pointerLockError);
    document.addEventListener('visibilitychange', visibilityChange);
    window.addEventListener('blur', suspend);
    window.addEventListener('focus', resume);
    window.addEventListener('keydown', keyDown, true);
    return () => {
      mountedRef.current = false;
      document.removeEventListener('pointerlockchange', pointerChange);
      document.removeEventListener('pointerlockerror', pointerLockError);
      document.removeEventListener('visibilitychange', visibilityChange);
      window.removeEventListener('blur', suspend);
      window.removeEventListener('focus', resume);
      window.removeEventListener('keydown', keyDown, true);
      window.clearTimeout(captureTimerRef.current);
      captureTimerRef.current = null;
      capturePendingRef.current = false;
      captureIdRef.current += 1;
      motionRequestIdRef.current += 1;
      motionPendingRef.current = false;
    };
  }, [cancelCapture, isTouch, pointerError, releasePointer, syncActivity]);

  useEffect(() => {
    const source = siteRef.current;
    const stage = stageRef.current;
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const panel = getPanelViewport(viewport.width, viewport.height, inputMode);
    const scroll = { x: 0, y: 0 };
    const previousOverflow = document.body.style.overflow;
    const sourceStyles = ['width', 'min-height', 'visibility'].map(name => ({
      name, value: source.style.getPropertyValue(name), priority: source.style.getPropertyPriority(name),
    }));
    const sourceAttributes = ['data-spatial-source', 'inert', 'aria-hidden'].map(name => ({
      name, value: source.getAttribute(name),
    }));
    document.body.style.overflow = 'hidden';
    source.style.width = `${panel.width}px`;
    source.style.minHeight = `${panel.height - WINDOW_BAR_HEIGHT}px`;
    source.style.visibility = 'hidden';
    source.setAttribute('data-spatial-source', '');
    source.setAttribute('inert', '');
    source.setAttribute('aria-hidden', 'true');
    let cancelled = false;
    let failed = false;
    let runtime;
    const fail = () => {
      if (cancelled || failed) return;
      failed = true;
      if (motionPromptOpenRef.current) setShowMotionPrompt(false);
      motionRequestIdRef.current += 1;
      motionPendingRef.current = false;
      setMotionPending(false);
      cancelCapture();
      if (document.pointerLockElement === stage) document.exitPointerLock();
      runtime?.dispose();
      runtime = null;
      runtimeRef.current = null;
      setStatus('error');
      setMessage('The 3D view couldn’t start. Check that hardware acceleration is enabled, then try again.');
    };
    setStatus('loading');
    setLoadProgress(0);
    setLoadPhase('downloading');
    loadWorld().then(({ createPortfolioWorld }) => {
      if (cancelled) return;
      runtime = createPortfolioWorld({ container: stage, source, viewport, scroll, inputMode,
        dockElement, onError: fail,
        onProgress: (progress) => {
          if (!cancelled && !failed && !runtimeRef.current) setLoadProgress(Math.floor(progress * 100));
        },
        onPreparing: () => {
          if (cancelled || failed || runtimeRef.current) return;
          // Commit the phase before asset parsing blocks the main thread.
          flushSync(() => setLoadPhase('preparing'));
        },
        // Defer activation until construction finishes, even for cached assets.
        // Keeping runtimeRef empty also blocks input and status changes during loading.
        onReady: () => Promise.resolve().then(() => {
          if (cancelled || failed || runtimeRef.current) return;
          runtimeRef.current = runtime;
          runtime.setMotionEnabled(motionEnabledRef.current);
          runtime.setBrowserVisible(browserVisibleRef.current);
          syncActivity();
          // Commit the UI before the browser paints the completed WebGL frame.
          flushSync(() => setStatus(isTouch ? 'active' : 'free'));
        }).catch(fail),
      });
      if (failed) { runtime.dispose(); runtime = null; }
    }).catch(fail);
    return () => {
      cancelled = true;
      runtime?.dispose();
      runtimeRef.current = null;
      if (document.pointerLockElement === stage) document.exitPointerLock();
      document.body.style.overflow = previousOverflow;
      sourceStyles.forEach(({ name, value, priority }) => {
        if (value) source.style.setProperty(name, value, priority);
        else source.style.removeProperty(name);
      });
      sourceAttributes.forEach(({ name, value }) => {
        if (value === null) source.removeAttribute(name);
        else source.setAttribute(name, value);
      });
    };
  }, [attempt, siteRef, inputMode, isTouch, dockElement, cancelCapture, syncActivity]);

  useEffect(() => {
    if (!message || status === 'error') return undefined;
    const timer = window.setTimeout(() => setMessage(''), 5000);
    return () => window.clearTimeout(timer);
  }, [status, message]);

  return (
    <div className="world-overlay" data-input-mode={inputMode} data-state={status} role="region" aria-label="3D portfolio"
      onClickCapture={capturePageClick}>
      <div ref={stageRef} className="world-stage" data-state={status} tabIndex={0}
        inert={status === 'loading' || motionPromptOpen ? '' : undefined}
        aria-hidden={status === 'loading' || motionPromptOpen ? 'true' : undefined}
        aria-label={isTouch ? 'Tap dock apps and page buttons. Swipe the page to scroll.'
          : 'Click the environment or page to look around. Escape or Command releases the cursor. Aim at buttons and click to interact.'} />
      {createPortal(<QuestDock
        browserVisible={browserVisible}
        onToggleBrowser={toggleBrowser}
        showMotionToggle={isTouch && supportsMotion}
        motionEnabled={motionEnabled}
        motionPending={motionPending || checkingMotionPermission}
        onToggleMotion={toggleMotion}
      />, dockElement)}
      {!isTouch && status === 'active' && <div className="world-reticle" aria-hidden="true" />}
      {status === 'loading' && (
        <div className="world-loading" data-phase={loadPhase}>
          <div className="world-loading-label" aria-hidden="true">
            <span>{loadPhase === 'preparing' ? 'Preparing world...' : 'Loading world'}</span>
            {loadPhase === 'downloading' && <span>{loadProgress}%</span>}
          </div>
          <div className="world-loading-track" role="progressbar"
            aria-label={loadPhase === 'preparing' ? 'Preparing world...' : 'Loading world'}
            aria-valuemin={0} aria-valuemax={100}
            aria-valuenow={loadPhase === 'downloading' ? loadProgress : undefined}>
            <div className="world-loading-fill"
              style={loadPhase === 'downloading' ? { transform: `scaleX(${loadProgress / 100})` } : undefined} />
          </div>
        </div>
      )}
      {motionPromptOpen && <MotionPermissionPrompt pending={motionPending} onEnable={enableMotion}
        onDismiss={dismissMotionPrompt} stageRef={stageRef} />}
      {status === 'error' && (
        <section className="world-error" role="alert">
          <p>{message}</p>
          <button className="world-primary-button" onClick={() => { setMessage(''); setAttempt(value => value + 1); }}>Retry 3D view</button>
        </section>
      )}
      {message && status !== 'error' && <p className="world-motion-notice" role="status">{message}</p>}
    </div>
  );
}
